import { useCart } from '../../context/CartContext';
import { Link } from 'react-router-dom';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';

function resolveMedia(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
}

const TYPE_LABELS = { physical:'Physical', digital:'Digital', bundle:'Bundle', service:'Service' };
const TYPE_COLORS = { physical:'#e8a43a', digital:'#4e9cf5', bundle:'#3ecf8e', service:'#a78bfa' };

export default function CartDrawer() {
  const { items, count, total, currency, removeItem, updateQty, clearCart, drawerOpen, setDrawerOpen } = useCart();

  if (!drawerOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div onClick={() => setDrawerOpen(false)}
        style={{ position:'fixed', inset:0, zIndex:800, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)', animation:'fadeIn 0.2s ease' }}/>

      {/* Drawer */}
      <div style={{ position:'fixed', top:0, right:0, bottom:0, zIndex:801, width:'100%', maxWidth:420, background:'var(--bg-1)', borderLeft:'1px solid var(--border)', display:'flex', flexDirection:'column', animation:'slideInRight 0.25s ease' }}>

        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'1.05rem', fontWeight:600 }}>
              Your Cart
            </div>
            <div style={{ fontSize:'0.75rem', color:'var(--t-muted)', marginTop:2 }}>
              {count === 0 ? 'Empty' : `${count} item${count>1?'s':''}`}
            </div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {items.length > 0 && (
              <button onClick={clearCart}
                style={{ fontSize:'0.75rem', color:'var(--t-muted)', background:'none', border:'1px solid var(--border)', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s' }}
                onMouseEnter={e=>{e.currentTarget.style.color='#ef4444';e.currentTarget.style.borderColor='rgba(240,96,96,0.4)';}}
                onMouseLeave={e=>{e.currentTarget.style.color='var(--t-muted)';e.currentTarget.style.borderColor='var(--border)';}}>
                Clear all
              </button>
            )}
            <button onClick={() => setDrawerOpen(false)}
              style={{ width:32, height:32, borderRadius:8, background:'var(--bg-2)', border:'1px solid var(--border)', color:'var(--t-muted)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', transition:'all 0.15s' }}
              onMouseEnter={e=>{e.currentTarget.style.color='var(--t-primary)';}}
              onMouseLeave={e=>{e.currentTarget.style.color='var(--t-muted)';}}>
              ✕
            </button>
          </div>
        </div>

        {/* Items */}
        <div style={{ flex:1, overflowY:'auto', padding:'16px 24px', display:'flex', flexDirection:'column', gap:12 }}>
          {items.length === 0 ? (
            <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16, padding:'60px 0' }}>
              <div style={{ fontSize:'3rem', opacity:0.15 }}>◈</div>
              <div style={{ color:'var(--t-muted)', fontSize:'0.9rem', textAlign:'center', lineHeight:1.6 }}>
                Your cart is empty.<br/>Add something from the marketplace.
              </div>
              <button onClick={() => setDrawerOpen(false)} style={{ padding:'10px 20px', borderRadius:8, border:'1px solid var(--border)', background:'transparent', color:'var(--t-secondary)', cursor:'pointer', fontSize:'0.85rem', fontFamily:'inherit', transition:'all 0.15s' }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--gold)';e.currentTarget.style.color='var(--gold)';}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--t-secondary)';}}>
                Browse Marketplace
              </button>
            </div>
          ) : items.map(item => {
            const img = resolveMedia(item.image);
            const color = TYPE_COLORS[item.type] || 'var(--gold)';
            const itemKey = item.variant ? `${item.id}_${item.variant}` : item.id;
            return (
              <div key={itemKey} style={{ display:'flex', gap:12, padding:'14px', background:'var(--bg-2)', borderRadius:12, border:'1px solid var(--border)', position:'relative' }}>

                {/* Image */}
                <div style={{ width:56, height:56, borderRadius:8, overflow:'hidden', background:'var(--bg-3)', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {img ? (
                    <img src={img} alt={item.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                  ) : (
                    <div style={{ fontSize:'1.4rem', opacity:0.3 }}>◈</div>
                  )}
                </div>

                {/* Info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8, marginBottom:4 }}>
                    <div style={{ fontSize:'0.88rem', fontWeight:600, lineHeight:1.3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.name}</div>
                    <button onClick={() => removeItem(item.id, item.variant)}
                      style={{ color:'var(--t-muted)', background:'none', border:'none', cursor:'pointer', fontSize:'0.85rem', flexShrink:0, lineHeight:1, padding:0, transition:'color 0.15s' }}
                      onMouseEnter={e=>e.currentTarget.style.color='#ef4444'}
                      onMouseLeave={e=>e.currentTarget.style.color='var(--t-muted)'}>✕</button>
                  </div>

                  {/* Type + variant */}
                  <div style={{ display:'flex', gap:6, marginBottom:8, flexWrap:'wrap' }}>
                    <span style={{ fontSize:'0.65rem', fontWeight:600, padding:'2px 7px', borderRadius:20, background:`${color}15`, color, border:`1px solid ${color}30`, textTransform:'capitalize' }}>
                      {TYPE_LABELS[item.type]||item.type}
                    </span>
                    {item.variant && (
                      <span style={{ fontSize:'0.65rem', color:'var(--t-muted)', padding:'2px 7px', borderRadius:20, background:'var(--bg-3)', border:'1px solid var(--border)' }}>
                        {item.variant}
                      </span>
                    )}
                  </div>

                  {/* Price + qty */}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div style={{ fontSize:'0.88rem', fontWeight:700, color }}>
                      {item.is_free ? 'Free' : `${item.currency} ${Number(item.price * item.qty).toLocaleString()}`}
                    </div>

                    {/* Qty control — only for physical */}
                    {item.type === 'physical' ? (
                      <div style={{ display:'flex', alignItems:'center', gap:0, background:'var(--bg-3)', borderRadius:8, border:'1px solid var(--border)', overflow:'hidden' }}>
                        <button onClick={() => updateQty(item.id, item.variant, item.qty - 1)}
                          style={{ width:28, height:28, background:'none', border:'none', cursor:'pointer', color:'var(--t-muted)', fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s' }}
                          onMouseEnter={e=>{e.currentTarget.style.background='var(--bg-2)';e.currentTarget.style.color='var(--t-primary)';}}
                          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color='var(--t-muted)';}}>−</button>
                        <span style={{ width:28, textAlign:'center', fontSize:'0.82rem', fontWeight:600 }}>{item.qty}</span>
                        <button onClick={() => updateQty(item.id, item.variant, item.qty + 1)}
                          style={{ width:28, height:28, background:'none', border:'none', cursor:'pointer', color:'var(--t-muted)', fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s' }}
                          onMouseEnter={e=>{e.currentTarget.style.background='var(--bg-2)';e.currentTarget.style.color='var(--t-primary)';}}
                          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color='var(--t-muted)';}}>+</button>
                      </div>
                    ) : (
                      <span style={{ fontSize:'0.72rem', color:'var(--t-muted)' }}>×1</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div style={{ padding:'20px 24px', borderTop:'1px solid var(--border)', flexShrink:0, display:'flex', flexDirection:'column', gap:14 }}>

            {/* Subtotal */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:'0.82rem', color:'var(--t-muted)' }}>Subtotal ({count} item{count>1?'s':''})</span>
              <span style={{ fontFamily:'var(--font-display)', fontSize:'1.2rem', fontWeight:700, color:'var(--gold)' }}>
                {currency} {total.toLocaleString()}
              </span>
            </div>

            <div style={{ fontSize:'0.72rem', color:'var(--t-muted)', lineHeight:1.6 }}>
              Shipping and taxes calculated at checkout.
            </div>

            {/* CTA */}
            <Link to="/checkout" onClick={() => setDrawerOpen(false)}
              style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'14px', background:'var(--gold)', color:'#080a0d', borderRadius:10, fontWeight:700, fontSize:'0.95rem', textDecoration:'none', transition:'all 0.18s', textAlign:'center' }}
              onMouseEnter={e=>e.currentTarget.style.background='#f0b840'}
              onMouseLeave={e=>e.currentTarget.style.background='var(--gold)'}>
              Proceed to Checkout →
            </Link>

            <button onClick={() => setDrawerOpen(false)}
              style={{ padding:'11px', borderRadius:10, border:'1px solid var(--border)', background:'transparent', color:'var(--t-secondary)', cursor:'pointer', fontSize:'0.88rem', fontFamily:'inherit', transition:'all 0.15s' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--border-2)';e.currentTarget.style.color='var(--t-primary)';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--t-secondary)';}}>
              Continue Shopping
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
        @keyframes slideInRight { from { transform:translateX(100%); } to { transform:translateX(0); } }
      `}</style>
    </>
  );
}
